import React, { useState } from "react";

import { styled } from '@mui/material/styles';

import { useQuery, gql } from "@apollo/client";
import {
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Toolbar,
  Box,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Icon,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { SecuredNavLink } from "../HOC/CustomComponents/Secured";

import clsx from "clsx";
import FullScreenLoading from "../HOC/FunctionComponents/LoadingPages/FullScreenLoading";
import { LIST_SHIPPING_SERVICES } from "./Graphql";
import { useTranslation } from "react-i18next";
import emptyList from "../../assets/Image/received.png";
import CellLink from "../HOC/CustomComponents/CellLink";
import MUITablePagination from "../HOC/MUI/TablePagination/MUITablePagination";
import { FixedTableCell } from "../HOC/CustomComponents/FixedTableCell";
import TableFixedHeaderWraper from "../HOC/CustomComponents/TableWithFixedHeader";

const PREFIX = 'ShippingServicesList';

const classes = {
  appBar: `${PREFIX}-appBar`,
  title: `${PREFIX}-title`,
  paper: `${PREFIX}-paper`,
  main: `${PREFIX}-main`,
  mainContainer: `${PREFIX}-mainContainer`,
  iconColor: `${PREFIX}-iconColor`
};

const StyledGrid = styled(Grid)((
  {
    theme
  }
) => ({
  [`& .${classes.appBar}`]: {
    position: "relative",
  },

  [`& .${classes.title}`]: {
    flex: 1,
  },

  [`& .${classes.paper}`]: {
    display: "grid",
    width: "100%",
    borderRadius: 0,
  },

  [`& .${classes.main}`]: {
    marginTop: theme.spacing(2),
  },

  [`&.${classes.mainContainer}`]: {
    width: "100%",
  },

  [`& .${classes.iconColor}`]: {
    color: theme.palette.success.main,
  }
}));

const ShippingServicesList = (props) => {
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const { t } = useTranslation();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const { data, loading } = useQuery(
    gql`
      ${LIST_SHIPPING_SERVICES.query}
    `,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
      variables: {
        first: rowsPerPage,
        page: page + 1,
      },
      onCompleted: (data) => {},
    }
  );

  const listShippingServices = data?.listShippingServices?.data;
  let tableBody;

  tableBody = (
    <StyledGrid
      container
      item
      xs={12}
      justifyContent="center"
      className={classes.mainContainer}
    >
      {/* *******Table******* */}
      <Paper className={clsx(classes.paper)}>
        <Toolbar className={clsx(classes.toolbar)} variant="dense">
          <Typography className={classes.title} color="inherit" variant="h6">
            {t("shippingServicesList")}
          </Typography>
          <SecuredNavLink
            permission="shipping.shipping_service.create"
            to={{ pathname: `/admin/shipping-services/create` }}
          >
            <Tooltip title="Add ShippingService">
              <IconButton aria-label="Add shippingService" size="large">
                <Add color="primary" />
              </IconButton>
            </Tooltip>
          </SecuredNavLink>
        </Toolbar>

        {loading || listShippingServices?.length === 0 ? (
          <Grid container item justifyContent="center" className={classes.main}>
            {loading ? (
              <FullScreenLoading minHeight={30} />
            ) : (
              <Box textAlign="center">
                <img width="50%" src={emptyList} alt={"delivery box"} />
                <Box
                  component={Typography}
                  variant="h6"
                  color="text.secondary"
                  marginTop="0"
                >
                  {t("searchNoResult")}
                </Box>
              </Box>
            )}
          </Grid>
        ) : (
          <TableFixedHeaderWraper>
            <Table aria-labelledby="tableTitle" aria-label="enhanced table">
              <TableHead>
                <TableRow>
                  <FixedTableCell>{t("name")}</FixedTableCell>
                  <FixedTableCell>{t("code")}</FixedTableCell>
                  <FixedTableCell>{t("shippingDays")}</FixedTableCell>
                  <FixedTableCell>{t("branch")}</FixedTableCell>
                  <FixedTableCell>{t("deliveryAccount")}</FixedTableCell>
                  <FixedTableCell>{t("collectionAccount")}</FixedTableCell>
                  <FixedTableCell>{t("commissionAccount")}</FixedTableCell>
                  <FixedTableCell>{t("active")}</FixedTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listShippingServices &&
                  listShippingServices?.map((row, index) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                        <CellLink
                          pathname={`/admin/shipping-services/${row?.id}`}
                        >
                          {row?.name}
                        </CellLink>

                        <FixedTableCell>{row?.code}</FixedTableCell>
                        <FixedTableCell>{row?.days}</FixedTableCell>
                        <FixedTableCell>{row?.branch?.name}</FixedTableCell>
                        <FixedTableCell>
                          {row?.deliveryAccount?.name}
                        </FixedTableCell>
                        <FixedTableCell>
                          {row?.collectionAccount?.name}
                        </FixedTableCell>
                        <FixedTableCell>
                          {row?.commissionAccount?.name}
                        </FixedTableCell>
                        <FixedTableCell>
                          {row?.active ? (
                            <Icon className={classes.iconColor}>
                              check_circle_outline
                            </Icon>
                          ) : (
                            <Icon color="error">highlight_off</Icon>
                          )}
                        </FixedTableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableFixedHeaderWraper>
        )}
        <MUITablePagination
          count={data?.listShippingServices?.paginatorInfo?.total}
          rowsPerPage={rowsPerPage}
          page={!data?.listShippingServices ? 0 : page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </StyledGrid>
  );

  return tableBody;
};

export default ShippingServicesList;
